import { useCallback, useState } from 'react';
import { apiErrorMessage } from '../lib/api/apiErrorMessage';
import { sharepointClient } from '../lib/connect/clients';
// Identity is NOT sent. enies-lobby binds user_id and tenant_id to the gateway
// principal on every SharePoint RPC and clears any caller-supplied access
// token, so anything this passed would be overwritten server-side. The userId
// and tenantId parameters below are kept because four call sites pass them and
// the values are still what the UI displays — they just no longer travel.
function timestampToString(ts) {
    if (!ts) return '';
    return new Date(Number(ts.seconds) * 1000 + ts.nanos / 1000000).toISOString();
}
function mapSite(site) {
    return {
        site_id: site.siteId,
        site_name: site.siteName,
        site_url: site.siteUrl,
        permission_level: site.permissionLevel,
        roles: site.roles,
        last_checked: timestampToString(site.lastChecked)
    };
}
function mapPermission(p) {
    return {
        allowed: p.allowed,
        reason: p.reason,
        cached: p.cached,
        checked_at: timestampToString(p.checkedAt),
        metadata: p.metadata ? p.metadata.toJson() : undefined
    };
}
function mapDrive(d) {
    return {
        id: d.id,
        name: d.name,
        drive_type: d.driveType,
        web_url: d.webUrl,
        total_size: Number(d.totalSize),
        used_size: Number(d.usedSize),
        item_count: Number(d.itemCount)
    };
}
function mapItem(i) {
    return {
        id: i.id,
        name: i.name,
        web_url: i.webUrl,
        size: Number(i.size),
        mime_type: i.mimeType,
        is_folder: i.isFolder,
        child_count: i.childCount,
        created_by: i.createdBy,
        last_modified_by: i.lastModifiedBy,
        created_at: timestampToString(i.createdAt),
        modified_at: timestampToString(i.modifiedAt),
        drive_id: i.driveId,
        site_id: i.siteId,
        parent_path: i.parentPath,
        resource_id: i.resourceId
    };
}
function mapHit(h) {
    return {
        hit_id: h.hitId,
        name: h.name,
        web_url: h.webUrl,
        summary: h.summary,
        site_id: h.siteId,
        drive_id: h.driveId,
        item_id: h.itemId,
        resource_id: h.resourceId,
        size: Number(h.size),
        mime_type: h.mimeType,
        last_modified_by: h.lastModifiedBy,
        modified_at: timestampToString(h.modifiedAt),
        rank: h.rank
    };
}
export function useSharePoint() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Every call shares this shape: flag loading, clear the previous error, and
    // on failure record a message and hand back null rather than throwing. That
    // was duplicated eight times around eight fetches; with the transport gone it
    // is the only thing left worth sharing.
    const run = useCallback(async (fallback, fn)=>{
        setLoading(true);
        setError(null);
        try {
            return await fn();
        } catch (err) {
            setError(apiErrorMessage(err, fallback));
            return null;
        } finally{
            setLoading(false);
        }
    }, []);
    const getUserSiteAccess = useCallback((userId, tenantId)=>run('Failed to get site access', async ()=>{
            const res = await sharepointClient.getUserSiteAccess({});
            return {
                user_id: res.userId || userId,
                tenant_id: res.tenantId || tenantId,
                sites: res.sites.map(mapSite),
                updated_at: timestampToString(res.updatedAt)
            };
        }), [
        run
    ]);
    const checkDocumentPermission = useCallback((_userId, _tenantId, resourceId)=>run('Failed to check document permission', async ()=>mapPermission(await sharepointClient.checkDocumentPermission({
                resourceId
            }))), [
        run
    ]);
    const checkMultipleDocuments = useCallback((_userId, _tenantId, resourceIds)=>run('Failed to check documents', async ()=>{
            const res = await sharepointClient.batchCheckDocumentPermission({
                resourceIds
            });
            return {
                results: Object.fromEntries(Object.entries(res.results).map(([id, r])=>[
                        id,
                        mapPermission(r)
                    ])),
                checked_at: timestampToString(res.checkedAt),
                total_checked: res.totalChecked
            };
        }), [
        run
    ]);
    const filterDocumentsByACL = useCallback((_userId, _tenantId, documents)=>run('Failed to filter documents', async ()=>{
            const res = await sharepointClient.filterDocumentsByACL({
                documents: documents.map((d)=>({
                        id: d.id,
                        title: d.title,
                        resourceId: d.resource_id
                    }))
            });
            return {
                authorized_documents: res.authorizedDocuments.map((d)=>({
                        id: d.id,
                        title: d.title,
                        resource_id: d.resourceId
                    })),
                total_requested: res.totalRequested,
                total_authorized: res.totalAuthorized,
                total_denied: res.totalDenied,
                filter_time_ms: Number(res.filterTimeMs),
                cache_hits: res.cacheHits
            };
        }), [
        run
    ]);
    const listSiteDrives = useCallback((siteId)=>run('Failed to list drives', async ()=>{
            const res = await sharepointClient.listSiteDrives({
                siteId
            });
            return res.drives.map(mapDrive);
        }), [
        run
    ]);
    const listDriveItems = useCallback((_siteId, driveId, folderId, pageSize, pageToken)=>run('Failed to list documents', async ()=>{
            const res = await sharepointClient.listDriveItems({
                driveId,
                itemId: folderId ?? '',
                pageSize: pageSize ?? 0,
                pageToken: pageToken ?? ''
            });
            return {
                items: res.items.map(mapItem),
                next_page_token: res.nextPageToken,
                total_count: res.totalCount
            };
        }), [
        run
    ]);
    const searchDocuments = useCallback((query, pageSize, offset)=>run('Failed to search documents', async ()=>{
            const res = await sharepointClient.searchDocuments({
                query,
                pageSize: pageSize ?? 0,
                offset: offset ?? 0
            });
            return {
                hits: res.hits.map(mapHit),
                total_count: res.totalCount,
                more_results_available: res.moreResultsAvailable
            };
        }), [
        run
    ]);
    const downloadDocument = useCallback(async (siteId, driveId, itemId)=>{
        await run('Failed to download document', async ()=>{
            const res = await sharepointClient.getDriveItemContent({
                siteId,
                driveId,
                itemId
            });
            // The RPC answers with bytes when it has them and a URL when the file is
            // better fetched straight from Graph. The REST route made the same
            // distinction by Content-Disposition; this one is explicit about it.
            if (res.content.length > 0) {
                const blob = new Blob([
                    res.content
                ], {
                    type: res.mimeType || 'application/octet-stream'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = res.fileName || 'document';
                a.click();
                URL.revokeObjectURL(url);
            } else if (res.downloadUrl) {
                window.open(res.downloadUrl, '_blank');
            }
            return null;
        });
    }, [
        run
    ]);
    const clearError = useCallback(()=>{
        setError(null);
    }, []);
    return {
        loading,
        error,
        clearError,
        getUserSiteAccess,
        checkDocumentPermission,
        checkMultipleDocuments,
        filterDocumentsByACL,
        listSiteDrives,
        listDriveItems,
        searchDocuments,
        downloadDocument
    };
}
